import React, { createContext, useContext, useMemo } from "react";
import { Control, FieldArrayWithId, useFieldArray } from "react-hook-form";
import { AssessmentEditFormModel } from "./AssessmentEditContext";
import { QuestionModel } from "app/services/api/types/forms/models/QuestionModel";
import { useAssessmentEditPage } from "./AssessmentEditPageContext";

export interface SectionQuestionsContextValue {
  questionFields: FieldArrayWithId<
    AssessmentEditFormModel,
    `paginatedSections.${number}.sections.${number}.questions`,
    "key-id"
  >[];
  appendQuestion: (value: QuestionModel) => void;
  removeQuestion: (index: number) => void;
  updateQuestion: (index: number, value: QuestionModel) => void;
  moveQuestion: (from: number, to: number) => void;
}

const SectionQuestionsContext = createContext<
  SectionQuestionsContextValue | undefined
>(undefined);

export function useSectionQuestions() {
  const ctx = useContext(SectionQuestionsContext);
  if (!ctx)
    throw new Error(
      "useSectionQuestions must be used within SectionQuestionsProvider",
    );
  return ctx;
}

interface ProviderProps {
  control: Control<AssessmentEditFormModel>;
  sectionIndex: number;
  children: React.ReactNode;
}

export function SectionQuestionsProvider({
  control,
  sectionIndex,
  children,
}: ProviderProps) {
  const { pageIndex } = useAssessmentEditPage();
  const name =
    `paginatedSections.${pageIndex}.sections.${sectionIndex}.questions` as const;
  const { fields, append, remove, update, move } = useFieldArray<
    AssessmentEditFormModel,
    typeof name,
    "key-id"
  >({
    control,
    name,
    keyName: "key-id",
  });

  const value = useMemo<SectionQuestionsContextValue>(
    () => ({
      questionFields: fields,
      appendQuestion: (v: QuestionModel) => append(v),
      removeQuestion: (index: number) => remove(index),
      updateQuestion: (index: number, v: QuestionModel) => update(index, v),
      moveQuestion: (from: number, to: number) => move(from, to),
    }),
    [fields],
  );

  return (
    <SectionQuestionsContext.Provider value={value}>
      {children}
    </SectionQuestionsContext.Provider>
  );
}
